import { apiService } from './api';
import { User } from '../types';

export interface UserBasic {
  user_id: string;
  email: string;
  display_name?: string | null;
  /** False for deactivated staff — their name still shows on history. */
  is_active?: boolean;
}

class UserService {
  private baseUrl = '/users';
  // Session-lifetime cache — user emails don't change often enough to
  // justify refetching every time an Order/Product detail view resolves
  // the same handful of staff ids.
  private cache = new Map<string, UserBasic>();
  private pending = new Map<string, Promise<UserBasic | null>>();

  // Resolve a user id to its display email. Returns null (not throw) if
  // the user can't be found or resolved — callers should treat that the
  // same as "unknown", not surface an error for what's just a label.
  async getUserBasic(userId: string): Promise<UserBasic | null> {
    const cached = this.cache.get(userId);
    if (cached) return cached;

    const inFlight = this.pending.get(userId);
    if (inFlight) return inFlight;

    const request = apiService
      .get<UserBasic>(`${this.baseUrl}/${userId}`)
      .then((user) => {
        this.cache.set(userId, user);
        return user;
      })
      .catch(() => null)
      .finally(() => {
        this.pending.delete(userId);
      });

    this.pending.set(userId, request);
    return request;
  }

  // All staff accounts as id+email pairs — used to populate "created by"
  // style filter dropdowns. Not cached long-term (unlike getUserBasic)
  // since callers only need this for a one-off dropdown population.
  async listUsers(): Promise<UserBasic[]> {
    const users = await apiService.get<UserBasic[]>(this.baseUrl);
    users.forEach((user) => this.cache.set(user.user_id, user));
    return users;
  }

  /** Set your own display name (blank clears it). */
  async updateMyName(displayName: string): Promise<User> {
    const user = await apiService.patch<User>(`${this.baseUrl}/me`, { display_name: displayName.trim() || null });
    this.cache.delete(user.user_id);
    return user;
  }

  /** Owner only: set someone's display name (blank clears it). */
  async setUserName(userId: string, displayName: string): Promise<User> {
    const user = await apiService.post<User>(`/admin/users/${userId}/name`, { display_name: displayName.trim() || null });
    this.cache.delete(user.user_id);
    return user;
  }
}

export const userService = new UserService();
